// Pure helpers for managing products inside a contract basket.
// Extracted so race-condition behaviour can be unit-tested without rendering
// the full ContractFormPage (which depends on TanStack Router/Query state).

import type { BasketWithTempId } from "./contract-form-page";
import type { ProductBrief, ProductItem } from "./contracts-api";

/**
 * Compute new ProductBriefs to append to a basket given a freshly-selected list
 * of ProductItems. Deduplicates by `bloz07` against existing basket products.
 *
 * Returns an empty array when the basket is missing — this guards against the
 * race seen in Sentry FARMASTER-FRONTEND-1C (17-04-2026): the
 * ProductSelectorModal stays open while the parent contract is refetched,
 * `setBaskets(...)` shrinks/replaces the array, and the cached `activeBasketIndex`
 * now points past the end. Calling `.products` on `undefined` previously
 * crashed the page; returning [] lets the caller close the modal cleanly.
 */
export function selectNewProductsForBasket(
	basket: BasketWithTempId | undefined,
	selectedProducts: ProductItem[],
): ProductBrief[] {
	if (!basket) return [];
	const existingBlozs = new Set(basket.products.map((p) => p.bloz07));
	return selectedProducts
		.filter((p) => !existingBlozs.has(p.bloz07))
		.map((p) => ({ bloz07: p.bloz07, name: p.nazwa }));
}
